import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Send as SendIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { decryptText, decryptAppId } from "../../../utils/encryption";
import axios from "axios";
import * as XLSX from "xlsx";

const DoAssessorLogistics = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ─── Decrypt URL Params ─────────────────────────────────────
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        return decryptText(encryptedOrgId);
      } catch {
        return encryptedOrgId;
      }
    }
    return null;
  };

  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        return decryptAppId(encryptedAppId);
      } catch {
        return encryptedAppId;
      }
    }
    return null;
  };

  const [orgId] = useState(getOrgIdFromQuery());
  const [appId] = useState(getAppIdFromQuery());
  const [assessors, setAssessors] = useState([]);
  const [selectedAssessors, setSelectedAssessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null); // 'send' or 'cancel'

  // ─── Load Assessors ──────────────────────────────────────────
  useEffect(() => {
    if (appId) {
      loadAssessors();
    }
  }, [appId]);

  const loadAssessors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/do/assessors/${appId}`
      );

      if (response.data.success) {
        setAssessors(response.data.data || []);
        // Clear selection when data reloads
        setSelectedAssessors([]);
      } else {
        toast.error(response.data.message || "Failed to load assessors");
      }
    } catch (error) {
      console.error("Error loading assessors:", error);
      toast.error("Failed to load assessors");
    } finally {
      setLoading(false);
    }
  };

  // ─── Checkbox Handlers (using LoginName as unique key) ──
  const handleCheckboxChange = (assessorId) => {
    setSelectedAssessors((prev) => {
      const id = String(assessorId);
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = assessors
        .map((a) => String(a.assessorId))
        .filter(Boolean);
      setSelectedAssessors(allIds);
    } else {
      setSelectedAssessors([]);
    }
  };

  // ─── Dialog Handlers ────────────────────────────────────────
  const openDialog = (action) => {
    if (selectedAssessors.length === 0) {
      toast.warning("Please select at least one assessor");
      return;
    }
    setDialogAction(action);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogAction(null);
  };

  const handleDialogConfirm = async () => {
    setDialogOpen(false);
    if (dialogAction === "send") {
      await handleSendToTravel();
    } else if (dialogAction === "cancel") {
      await handleCancelTravel();
    }
    setDialogAction(null);
  };

  // ─── Generate Excel File ─────────────────────────────────────
  const generateExcelFile = (selectedData, applicationNo, isCancellation = false) => {
    // Prepare data for Excel
    const excelData = selectedData.map((row, index) => ({
      "S.No": index + 1,
      "Lab Type": row.LabType || "",
      "Assessment Type": row.Assessmentname || "",
      "DO Name": row.DoName || "",
      "DO Contact No.": row.domobileno || "",
      "DO Email Id": row.doemailid || "",
      "Application No": row.ApplicationNo || "",
      "Laboratory Name": row.LaboratoryName || "",
      "Address": row.AddressLine1 || "",
      "Lab Contact Person": row.ContactPerson || "",
      "Lab Contact No": row.ContactMobile || "",
      "Lab Email Id": row.ContactEmail || "",
      "Assessor Id": row.LoginName || "",
      "Assessor": row.assessorName || "",
      "Capacity": row.capacity || "",
      "From Date": row.DateFrom ? formatDate(row.DateFrom) : "",
      "To Date": row.DateTo ? formatDate(row.DateTo) : "",
      "Assessor Email Id": row.OfficeEmail || "",
      "Assessor Contact": row.OfficeMobile || "",
      "Organisation": row.CurrentEmployer || "",
      "Origin City": row.OriginCity || "",
      "Onward Travel Date": row.OnwardTravelDate ? formatDate(row.OnwardTravelDate) : "",
      "Onward Flight No": row.OnwardFlightTrainNo || "",
      "Destination City": row.Destination || "",
      "Return Travel Date": row.ReturnTravelDate ? formatDate(row.ReturnTravelDate) : "",
      "Return Flight No": row.ReturnFlightTrainNo || "",
      "Accommodation": row.AccommodationRequired === "1" || row.AccommodationRequired === 1 ? "Yes" : "No",
      "Seat Preference": row.SeatPreference || "",
      "Meal Preference": row.MealPreference || "",
      "Remarks": row.Remarks || ""
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 8 }, { wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 15 },
      { wch: 25 }, { wch: 15 }, { wch: 30 }, { wch: 35 }, { wch: 20 },
      { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 30 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 30 }
    ];
    ws['!cols'] = colWidths;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Assessment Logistics");

    // Generate filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const prefix = isCancellation ? "Cancellation_" : "";
    const fileName = `${prefix}Assessment_Logistics_${appId}_${timestamp}.xlsx`;

    // Write to binary string
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

    // Convert to blob
    const buf = new ArrayBuffer(wbout.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < wbout.length; i++) {
      view[i] = wbout.charCodeAt(i) & 0xff;
    }

    return {
      blob: new Blob([buf], { type: "application/octet-stream" }),
      fileName: fileName
    };
  };

  // ─── API Actions ─────────────────────────────────────────────
  const handleSendToTravel = async () => {
    setSending(true);
    try {
      // Get selected assessors data
      const selectedData = assessors.filter((assessor) =>
        selectedAssessors.includes(assessor.assessorId?.toString())
      );

      if (selectedData.length === 0) {
        toast.error("No matching assessors found");
        setSending(false);
        return;
      }

      // Get application number
      const applicationNo = selectedData[0]?.ApplicationNo || appId;

      // Generate Excel file
      const { blob, fileName } = generateExcelFile(selectedData, applicationNo, false);

      // Prepare FormData
      const formData = new FormData();
      formData.append("appId", appId);
      formData.append("selectedAssessors", JSON.stringify(selectedAssessors));
      formData.append("applicationNo", applicationNo);
      formData.append("excelFile", blob, fileName);

      // Send to backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/do/send-to-travel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success("Travel request sent successfully to Q2T Team");
        await loadAssessors();
        setSelectedAssessors([]);
      } else {
        toast.error(response.data.message || "Failed to send request");
      }
    } catch (error) {
      console.error("Error sending to travel:", error);
      toast.error(
        error.response?.data?.message || "Failed to send travel request"
      );
    } finally {
      setSending(false);
    }
  };

  const handleCancelTravel = async () => {
    setSending(true);
    try {
      // Get selected assessors data
      const selectedData = assessors.filter((assessor) =>
        selectedAssessors.includes(assessor.assessorId?.toString())
      );

      if (selectedData.length === 0) {
        toast.error("No matching assessors found");
        setSending(false);
        return;
      }

      // Get application number
      const applicationNo = selectedData[0]?.ApplicationNo || appId;

      // Generate Excel file for cancellation
      const { blob, fileName } = generateExcelFile(selectedData, applicationNo, true);

      // Prepare FormData
      const formData = new FormData();
      formData.append("appId", appId);
      formData.append("selectedAssessors", JSON.stringify(selectedAssessors));
      formData.append("applicationNo", applicationNo);
      formData.append("excelFile", blob, fileName);

      // Send to backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/do/cancel-travel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success("Cancellation request sent successfully to Q2T Team");
        await loadAssessors();
        setSelectedAssessors([]);
      } else {
        toast.error(response.data.message || "Failed to send cancellation");
      }
    } catch (error) {
      console.error("Error canceling travel:", error);
      toast.error(
        error.response?.data?.message || "Failed to send cancellation request"
      );
    } finally {
      setSending(false);
    }
  };

  // ─── Format Date ─────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB");
    } catch {
      return dateStr;
    }
  };

  // ─── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={4}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        }}
      >
        {/* ─── Header ─────────────────────────────────────────── */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight="700" color="white">
            Assessor Logistics Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              color: "white",
            }}
          >
            Go Back
          </Button>
        </Box>

        {/* ─── Body ───────────────────────────────────────────── */}
        <Box sx={{ p: 3 }}>
          {/* Info Alert */}
          {assessors.length > 0 && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Total Assessors:</strong> {assessors.length}
                {selectedAssessors.length > 0 && (
                  <>
                    {" "}
                    | <strong>Selected:</strong> {selectedAssessors.length}
                  </>
                )}
              </Typography>
            </Alert>
          )}

          {assessors.length === 0 && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              No assessor logistics details found for this application.
            </Alert>
          )}

          {/* Table */}
          {assessors.length > 0 && (
            <>
              <TableContainer
                sx={{
                  maxHeight: 600,
                  mb: 3,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ bgcolor: "inherit" }}>
                        <Checkbox
                          checked={
                            selectedAssessors.length === assessors.length &&
                            assessors.length > 0
                          }
                          indeterminate={
                            selectedAssessors.length > 0 &&
                            selectedAssessors.length < assessors.length
                          }
                          onChange={handleSelectAll}
                          sx={{
                            color: "#1976d2",
                            "&.Mui-checked": { color: "#1976d2" },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        S.No
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Lab Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Assessment Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        App No
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Laboratory Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Address
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Assessor ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Assessor Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Capacity
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        From Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        To Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Contact
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Organisation
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Origin City
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Onward Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Onward Flight/Train
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Destination
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Return Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Return Flight/Train
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Accommodation
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Seat Preference
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Meal Preference
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Remarks
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "inherit" }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assessors.map((assessor, index) => {
                      const uniqueId = String(assessor.assessorId);
                      const isMailSent =
                        assessor.dosendmail === true ||
                        assessor.dosendmail === 1 ||
                        assessor.dosendmail === "1";

                      return (
                        <TableRow
                          key={uniqueId}
                          hover
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#fafafa" : "white",
                            "&:hover": { backgroundColor: "#e3f2fd" },
                          }}
                        >
                          <TableCell padding="checkbox">
                            {assessor.IsDOChange === true && (
                              <Checkbox
                                checked={selectedAssessors.includes(uniqueId)}
                                onChange={() =>
                                  handleCheckboxChange(assessor.assessorId)
                                }
                                sx={{
                                  color: "#1976d2",
                                  "&.Mui-checked": { color: "#1976d2" },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{assessor.LabType || "-"}</TableCell>
                          <TableCell>
                            {assessor.Assessmentname || "-"}
                          </TableCell>
                          <TableCell>{assessor.ApplicationNo || "-"}</TableCell>
                          <TableCell>
                            {assessor.LaboratoryName || "-"}
                          </TableCell>
                          <TableCell>{assessor.AddressLine1 || "-"}</TableCell>
                          <TableCell>{assessor.LoginName || "-"}</TableCell>
                          <TableCell>{assessor.assessorName || "-"}</TableCell>
                          <TableCell>{assessor.capacity || "-"}</TableCell>
                          <TableCell>{formatDate(assessor.DateFrom)}</TableCell>
                          <TableCell>{formatDate(assessor.DateTo)}</TableCell>
                          <TableCell>{assessor.OfficeMobile || "-"}</TableCell>
                          <TableCell>
                            {assessor.CurrentEmployer || "-"}
                          </TableCell>
                          <TableCell>{assessor.OriginCity || "-"}</TableCell>
                          <TableCell>
                            {formatDate(assessor.OnwardTravelDate)}
                          </TableCell>
                          <TableCell>
                            {assessor.OnwardFlightTrainNo || "-"}
                          </TableCell>
                          <TableCell>{assessor.Destination || "-"}</TableCell>
                          <TableCell>
                            {formatDate(assessor.ReturnTravelDate)}
                          </TableCell>
                          <TableCell>
                            {assessor.ReturnFlightTrainNo || "-"}
                          </TableCell>
                          <TableCell>
                            {Array.isArray(assessor.AccommodationRequired)
                              ? assessor.AccommodationRequired.some(
                                  (value) =>
                                    value === true ||
                                    value === 1 ||
                                    value === "1",
                                )
                                ? "Yes"
                                : "No"
                              : assessor.AccommodationRequired === true ||
                                  assessor.AccommodationRequired === 1 ||
                                  assessor.AccommodationRequired === "1"
                                ? "Yes"
                                : "No"}
                          </TableCell>
                          <TableCell>
                            {assessor.SeatPreference || "-"}
                          </TableCell>
                          <TableCell>
                            {assessor.MealPreference || "-"}
                          </TableCell>
                          <TableCell>{assessor.Remarks || "-"}</TableCell>
                          <TableCell>
                            {isMailSent ? (
                              <Chip
                                label="Sent"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            ) : (
                              <Chip
                                label="Pending"
                                color="warning"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Action Buttons */}
              <Box
                display="flex"
                gap={2}
                justifyContent="center"
                flexWrap="wrap"
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={() => openDialog("send")}
                  disabled={sending || selectedAssessors.length === 0}
                  sx={{
                    px: 4,
                    py: 1,
                    fontWeight: 700,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #0d47a1 0%, #0a3c7a 100%)",
                    },
                  }}
                >
                  {sending ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Send to Travel"
                  )}
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => openDialog("cancel")}
                  disabled={sending || selectedAssessors.length === 0}
                  sx={{
                    px: 4,
                    py: 1,
                    fontWeight: 700,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #e53935 0%, #b71c1c 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #b71c1c 0%, #880e0e 100%)",
                    },
                  }}
                >
                  Cancel Travel Request
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* ─── Confirmation Dialog ──────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>
          {dialogAction === "send"
            ? "Send to Travel Agency"
            : "Cancel Travel Request"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction === "send"
              ? `Are you sure you want to send travel details for ${selectedAssessors.length} assessor(s) to the travel agency?`
              : `Are you sure you want to cancel the travel request for ${selectedAssessors.length} assessor(s)?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            color={dialogAction === "send" ? "primary" : "error"}
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DoAssessorLogistics;